import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminService } from '../services/api';
import { QuestionWithVoters, QuestionStatus } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';

const statusOptions: { value: QuestionStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'answered', label: 'Answered' },
  { value: 'rejected', label: 'Rejected' },
];

const statusClasses: Record<QuestionStatus, string> = {
  pending: 'badge-pending',
  approved: 'badge-approved',
  answered: 'badge-answered',
  rejected: 'badge-rejected',
};

export default function AdminQuestionsPage() {
  const { getAccessToken } = useAuth();
  const [questions, setQuestions] = useState<QuestionWithVoters[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<QuestionStatus | 'all'>('all');
  const [showHidden, setShowHidden] = useState(true);
  const [expandedQuestion, setExpandedQuestion] = useState<number | null>(null);

  const fetchQuestions = useCallback(async () => {
    try {
      const token = await getAccessToken();
      if (!token) {
        setError('Unable to authenticate');
        return;
      }

      const response = await adminService.getQuestionsWithVoters(token);
      if (response.success && response.data) {
        setQuestions(response.data);
        setError(null);
      } else {
        setError(response.error || 'Failed to fetch questions');
      }
    } catch (err) {
      setError('Failed to fetch questions');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const handleStatusChange = async (questionId: number, newStatus: QuestionStatus) => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await adminService.updateStatus(token, questionId, newStatus);
      if (response.success) {
        setQuestions((prev) =>
          prev.map((q) => (q.id === questionId ? { ...q, status: newStatus } : q))
        );
      } else {
        alert(response.error || 'Failed to update status');
      }
    } catch (err) {
      console.error('Status update error:', err);
      alert('Failed to update status');
    }
  };

  const handleVisibilityToggle = async (questionId: number, isHidden: boolean) => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await adminService.updateVisibility(token, questionId, isHidden);
      if (response.success) {
        setQuestions((prev) =>
          prev.map((q) => (q.id === questionId ? { ...q, isHidden } : q))
        );
      } else {
        alert(response.error || 'Failed to update visibility');
      }
    } catch (err) {
      console.error('Visibility update error:', err);
      alert('Failed to update visibility');
    }
  };

  const filteredQuestions = questions
    .filter((q) => filterStatus === 'all' || q.status === filterStatus)
    .filter((q) => showHidden || !q.isHidden)
    .sort((a, b) => b.voteCount - a.voteCount);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Questions</h1>
          <p className="mt-1 text-sm text-gray-500">
            View all questions, change status, and manage visibility
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Status:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as QuestionStatus | 'all')}
              className="input w-auto"
            >
              <option value="all">All</option>
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
              className="rounded border-gray-300 text-blue-800 focus:ring-blue-700"
            />
            <span className="text-sm text-gray-700">Show hidden questions</span>
          </label>

          <div className="flex-1" />

          <span className="text-sm text-gray-500">
            Showing {filteredQuestions.length} of {questions.length} questions
          </span>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Questions List */}
      <div className="space-y-4">
        {filteredQuestions.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-12 text-gray-500">
              No questions match the current filters
            </div>
          </div>
        ) : (
          filteredQuestions.map((question) => (
            <div
              key={question.id}
              className={`card ${question.isHidden ? 'opacity-60 border-dashed' : ''}`}
            >
              <div className="card-body">
                <div className="flex gap-4">
                  {/* Vote count */}
                  <div className="flex flex-col items-center text-center min-w-[60px]">
                    <span className="text-2xl font-bold text-blue-800">
                      {question.voteCount}
                    </span>
                    <span className="text-xs text-gray-500">votes</span>
                  </div>

                  {/* Question content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <p className="text-gray-900 whitespace-pre-wrap">{question.questionText}</p>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {question.isHidden && (
                          <span className="badge badge-hidden">Hidden</span>
                        )}
                        <select
                          value={question.status}
                          onChange={(e) =>
                            handleStatusChange(question.id, e.target.value as QuestionStatus)
                          }
                          className={`input w-auto text-sm py-1 ${statusClasses[question.status]}`}
                        >
                          {statusOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500">
                      <span>
                        Asked by{' '}
                        <span className="font-medium">{question.createdByName}</span>
                      </span>
                      <span>{question.createdByEmail}</span>
                      <span>{formatDate(question.createdDate)}</span>
                    </div>

                    <div className="mt-4 flex items-center gap-4">
                      <button
                        onClick={() =>
                          handleVisibilityToggle(question.id, !question.isHidden)
                        }
                        className={`text-sm ${
                          question.isHidden
                            ? 'text-green-600 hover:text-green-700'
                            : 'text-yellow-600 hover:text-yellow-700'
                        }`}
                      >
                        {question.isHidden ? 'Unhide question' : 'Hide question'}
                      </button>

                      <button
                        onClick={() =>
                          setExpandedQuestion(
                            expandedQuestion === question.id ? null : question.id
                          )
                        }
                        className="text-sm text-blue-800 hover:text-blue-900"
                      >
                        {expandedQuestion === question.id ? 'Hide voters' : 'Show voters'} (
                        {question.voters.length})
                      </button>
                    </div>

                    {/* Voters list */}
                    {expandedQuestion === question.id && question.voters.length > 0 && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                          Voters ({question.voters.length})
                        </h4>
                        <div className="space-y-2">
                          {question.voters.map((voter, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between text-sm"
                            >
                              <div>
                                <span className="font-medium text-gray-900">{voter.name}</span>
                                <span className="text-gray-500 ml-2">{voter.email}</span>
                              </div>
                              <span className="text-gray-400 text-xs">
                                {formatDate(voter.voteDate)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {expandedQuestion === question.id && question.voters.length === 0 && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg text-center text-sm text-gray-500">
                        No votes yet
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
